import React, { useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { formatUtcDate } from "../utils/personaTransform";
import "./PersonaResponseView.css";

const PersonaGroup = ({ title, items }) => {
  const matchedCount = items.filter((item) => item.matched).length;

  if (!items.length) {
    return (
      <div className="pf-response__panel">
        <div className="pf-response__panel-title-row">
          <h3 className="pf-response__panel-title">{title}</h3>
          <span className="pf-response__count-chip">0</span>
        </div>
        <p className="pf-response__empty">No data available.</p>
      </div>
    );
  }

  return (
    <div className="pf-response__panel">
      <div className="pf-response__panel-title-row">
        <h3 className="pf-response__panel-title">{title}</h3>
        <div className="pf-response__count-group">
          <span className="pf-response__count-chip">{items.length} total</span>
          <span className="pf-response__count-chip matched">{matchedCount} matched</span>
        </div>
      </div>
      <div className="pf-response__cards">
        {items.map((item) => (
          <article key={item.id} className={`pf-response__card ${item.matched ? "is-matched" : "is-not-matched"}`}>
            <div className="pf-response__card-top">
              <h4>{item.displayName}</h4>
              <span className={`pf-response__status ${item.matched ? "matched" : "not-matched"}`}>
                {item.matched ? "Matched" : "Not matched"}
              </span>
            </div>
            <div className="pf-response__card-meta">
              <span>Score: {item.score}</span>
              <span>Rank: {item.rank ?? "-"}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};



const PersonaResponseView = ({ payload, error }) => {
  const [showRaw, setShowRaw] = useState(false);
  const [isExplainabilityOpen, setIsExplainabilityOpen] = useState(false);

  const explainabilityTotal = payload?.explainability?.length || 0;
  const explainabilityMatched = payload?.explainability?.filter((row) => row.matched).length || 0;
  const explainabilityUnmatched = explainabilityTotal - explainabilityMatched;

  const explainabilityRows = useMemo(() => {
    if (!payload) return [];
    return payload.explainability;
  }, [payload]);

  if (error) {
    return (
      <section className="pf-response pf-response--error">
        <div className="pf-response__error-icon">⚠️</div>
        <div className="pf-response__error-content">
          <h3>Request Failed</h3>
          <p>{error}</p>
          <p className="pf-response__error-help">
            Please check the client details or try again. This usually happens if the data is not yet available, the lab number is incorrect, or the service is temporarily down.
          </p>
        </div>
      </section>
    );
  }

  if (!payload) {
    return (
      <section className="pf-response pf-response--empty">
        <h3>Waiting for response</h3>
        <p>Select a client, enter lab number, and click Fetch Persona to view insights.</p>
      </section>
    );
  }

  const summary = payload.summary;
  const diet = payload.diet;

  return (
    <section className="pf-response" aria-live="polite">
      <div className="pf-response__hero">
        <div>
          <p className="pf-response__eyebrow">Persona output</p>
          <h2>{summary.finalPersona}</h2>
          <p className="pf-response__message">{summary.message}</p>
          <div className="pf-response__hero-stats">
            <span>Rules evaluated: {explainabilityTotal}</span>
            <span>Matched: {explainabilityMatched}</span>
            <span>Not matched: {explainabilityUnmatched}</span>
          </div>
        </div>
        <div className="pf-response__chips">
          <span className="pf-response__chip">Client: {summary.clientId}</span>
          <span className="pf-response__chip">Lab: {summary.labNo}</span>
          <span className="pf-response__chip">Created: {formatUtcDate(summary.createdAt)}</span>
          <span className="pf-response__chip">Primary: {summary.primaryPersona}</span>
          <span className="pf-response__chip">Secondary: {summary.secondaryPersona}</span>
          <span className="pf-response__chip">Hybrid: {summary.hybridPersona}</span>
        </div>
      </div>

      <div className="pf-response__panels">
        <PersonaGroup title="Primary Personas" items={payload.personas.primary} />
        <PersonaGroup title="Secondary Personas" items={payload.personas.secondary} />
        <PersonaGroup title="Hybrid Personas" items={payload.personas.hybrid} />
      </div>

      <div className="pf-response__panel">
        <div className="pf-response__panel-title-row">
          <h3 className="pf-response__panel-title">Recommended Diet</h3>
        </div>
        <p className="pf-response__panel-subtitle">
          Personalized nutrition guidance derived from matched biomarkers.
        </p>
        <div className="pf-response__diet-summary" style={{ marginBottom: 0 }}>
          <h4>{diet.name}</h4>
          <p>
            <strong>Focus:</strong> {diet.focus}
          </p>
          <p>
            <strong>Logic:</strong> {diet.baseLogic}
          </p>
        </div>
      </div>

      <div className="pf-response__panel">
        <div 
          className="pf-response__panel-header pf-response__panel-header--clickable"
          onClick={() => setIsExplainabilityOpen((prev) => !prev)}
        >
          <div>
            <h3 className="pf-response__panel-title">
              Explainability
              <span className="pf-response__toggle-icon">
                {isExplainabilityOpen ? " ▲" : " ▼"}
              </span>
            </h3>
            <p className="pf-response__panel-subtitle" style={{ marginBottom: 0 }}>
              Rule-by-rule logic trace for this persona outcome.
            </p>
          </div>

        </div>

        {isExplainabilityOpen && (
          <div className="pf-response__table-wrap">
            <Table responsive hover size="sm" className="pf-response__table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Test</th>
                  <th>Value</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {explainabilityRows.length ? (
                  explainabilityRows.map((row) => (
                    <tr key={row.id} className={row.matched ? "pf-response__row--matched" : "pf-response__row--unmatched"}>
                      <td className="pf-response__col-rule">{row.ruleName}</td>
                      <td className="pf-response__col-test">{row.test}</td>
                      <td className="pf-response__col-value">{String(row.value)}</td>
                      <td className="pf-response__col-reason">{row.reason}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="pf-response__empty-cell">
                      No explainability rows for selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <div className="pf-response__panel">
        <div className="pf-response__panel-header">
          <h3 className="pf-response__panel-title">Raw JSON</h3>
          <Button size="sm" variant="outline-dark" onClick={() => setShowRaw((prev) => !prev)}>
            {showRaw ? "Hide" : "Show"}
          </Button>
        </div>
        {showRaw ? (
          <pre className="pf-response__raw">{JSON.stringify(payload.raw, null, 2)}</pre>
        ) : (
          <p className="pf-response__raw-hint">Use this to verify exact API values.</p>
        )}
      </div>
    </section>
  );
};

export default PersonaResponseView;
